import { useState } from 'react';
import { IconUserCheck, IconUserOff } from '@tabler/icons-react';
import type { Capability, UserRole } from '@admitly/shared';
import {
  useBranchOptions,
  useClearCapabilityOverride,
  useDeactivateUser,
  useReactivateUser,
  useSetBranch,
  useSetCapabilityOverride,
  useSetRole,
  useUserCapabilities,
  type StaffUser,
} from '../../hooks/useStaff.js';
import {
  ASSIGNABLE_ROLES,
  CAP_LABELS,
  ROLE_LABELS,
  computeRows,
  flipAction,
} from '../../lib/capability-view.js';
import { useAuthStore } from '../../lib/auth.js';
import { ApiError } from '../../lib/api.js';
import { Button } from '../ui/Button.js';
import { Select } from '../ui/Select.js';
import { Switch } from '../ui/Switch.js';
import { SendModalShell } from '../send/SendModalShell.js';

/** Role pill — brand tokens (mirrors team-list.tsx). Shared with the staff list. */
export function RolePill({ role }: { role: UserRole }) {
  return (
    <span className="inline-flex items-center rounded-full border border-line bg-cream-deep/50 px-2.5 py-0.5 text-xs font-medium text-navy">
      {ROLE_LABELS[role] ?? role}
    </span>
  );
}

/** Active / Inactive badge (token form — no raw hex). */
export function StatusBadge({ active }: { active: boolean }) {
  return active ? (
    <span className="badge bg-success-bg text-success">Active</span>
  ) : (
    <span className="badge bg-danger-bg text-danger">Inactive</span>
  );
}

function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  loading,
  danger,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  body: string;
  confirmLabel: string;
  loading: boolean;
  danger?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <SendModalShell open={open} onClose={onCancel} blocking={loading}>
      <div className="p-6">
        <h2 className="font-serif text-xl text-ink tracking-tight">{title}</h2>
        <p className="mt-2 text-sm text-muted">{body}</p>
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="secondary" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button variant={danger ? 'danger' : 'primary'} loading={loading} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </SendModalShell>
  );
}

export function StaffDetailPanel({ staff }: { staff: StaffUser }) {
  const currentUserId = useAuthStore((s) => s.user?.id);
  const caps = useUserCapabilities(staff.id);
  const branchOptions = useBranchOptions(staff.id);
  const setRole = useSetRole();
  const setBranch = useSetBranch();
  const setOverride = useSetCapabilityOverride();
  const clearOverride = useClearCapabilityOverride();
  const deactivate = useDeactivateUser();
  const reactivate = useReactivateUser();

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [pendingRole, setPendingRole] = useState<UserRole | null>(null);
  const [pendingManageUsers, setPendingManageUsers] = useState(false);
  const [pendingSwitchOff, setPendingSwitchOff] = useState(false);

  const role: UserRole = caps.data?.role ?? staff.role;
  const overrides = caps.data?.overrides ?? [];
  const isSuperAdminTarget = role === 'super_admin';
  const isSelf = !!currentUserId && staff.id === currentUserId;
  const locked = isSuperAdminTarget || isSelf;
  const busy =
    setRole.isPending || setBranch.isPending || setOverride.isPending || clearOverride.isPending;

  // Current office as a branch id ('' = no office). Offices are unique by code
  // within a tenant, so match the read-only office on the row to its id.
  const currentBranchId =
    branchOptions.data?.branches?.find((b) => b.code === staff.office?.code)?.id ?? '';

  function toMessage(err: unknown): string {
    return err instanceof ApiError ? err.message : 'Something went wrong. Please try again.';
  }

  async function commitRole(next: UserRole) {
    setError(null);
    setSuccess(null);
    try {
      await setRole.mutateAsync({ userId: staff.id, role: next });
      setSuccess(`Role changed to ${ROLE_LABELS[next]}.`);
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setPendingRole(null);
    }
  }

  async function commitBranch(next: string) {
    setError(null);
    setSuccess(null);
    try {
      await setBranch.mutateAsync({ userId: staff.id, branchId: next || null });
      const picked = next
        ? branchOptions.data?.branches?.find((b) => b.id === next)
        : null;
      setSuccess(picked ? `Office set to ${picked.name}.` : 'Office cleared.');
    } catch (err) {
      setError(toMessage(err));
    }
  }

  async function performFlip(capability: Capability, next: boolean) {
    setError(null);
    setSuccess(null);
    const action = flipAction(role, capability, next);
    try {
      if (action.kind === 'clear') {
        await clearOverride.mutateAsync({ userId: staff.id, capability });
      } else {
        await setOverride.mutateAsync({ userId: staff.id, capability, granted: action.granted });
      }
      setSuccess(`${CAP_LABELS[capability]} updated.`);
    } catch (err) {
      setError(toMessage(err));
    }
  }

  function handleFlip(capability: Capability, next: boolean) {
    // Only two actions confirm; everything else saves instantly.
    if (capability === 'manage_users' && next) {
      setPendingManageUsers(true);
      return;
    }
    void performFlip(capability, next);
  }

  async function handleReset(capability: Capability) {
    setError(null);
    setSuccess(null);
    try {
      await clearOverride.mutateAsync({ userId: staff.id, capability });
      setSuccess(`${CAP_LABELS[capability]} reset to the role default.`);
    } catch (err) {
      setError(toMessage(err));
    }
  }

  async function confirmSwitchOff() {
    setError(null);
    setSuccess(null);
    try {
      await deactivate.mutateAsync(staff.id);
      setSuccess(`${staff.name} switched off.`);
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setPendingSwitchOff(false);
    }
  }

  async function handleSwitchOn() {
    setError(null);
    setSuccess(null);
    try {
      await reactivate.mutateAsync(staff.id);
      setSuccess(`${staff.name} switched on.`);
    } catch (err) {
      setError(toMessage(err));
    }
  }

  // manage_users can never be granted through this screen (the server refuses it
  // for any non-super_admin, and super_admins are locked here), so it never
  // appears as a toggle — filtered out for everyone.
  const rows = computeRows(role, overrides).filter((row) => row.capability !== 'manage_users');

  return (
    <div className="surface p-6">
      {/* Header (read-only) + switch-off/on control */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-serif text-2xl text-ink tracking-tight">{staff.name}</h2>
          <p className="text-sm text-muted mt-0.5">{staff.email}</p>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <RolePill role={role} />
            <StatusBadge active={staff.active} />
            <span className="text-xs text-muted">
              {staff.office ? `${staff.office.name} (${staff.office.code})` : 'No office'}
              {' · '}
              {staff.agency?.name ?? 'Master admin'}
            </span>
          </div>
        </div>
        {/* super_admin + self are locked (the 3d endpoint refuses them) — no control. */}
        {!locked &&
          (staff.active ? (
            <Button
              variant="danger"
              size="sm"
              leftIcon={<IconUserOff size={16} />}
              disabled={deactivate.isPending}
              onClick={() => setPendingSwitchOff(true)}
            >
              Switch off
            </Button>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<IconUserCheck size={16} />}
              loading={reactivate.isPending}
              onClick={() => void handleSwitchOn()}
            >
              Switch on
            </Button>
          ))}
      </div>

      {error && (
        <div className="mt-4 rounded-lg border border-danger/30 bg-danger-bg px-4 py-3 text-sm text-danger">
          {error}
        </div>
      )}
      {success && !error && (
        <div className="mt-4 rounded-lg border border-success/30 bg-success-bg px-4 py-3 text-sm text-success">
          {success}
        </div>
      )}

      {locked && (
        <div className="mt-4 rounded-lg border border-line bg-cream/60 px-4 py-3 text-sm text-muted">
          {isSuperAdminTarget
            ? 'Master admin — full access, not adjustable.'
            : "You can't change your own access."}
        </div>
      )}

      {caps.isLoading && <p className="mt-6 text-sm text-muted">Loading permissions…</p>}
      {caps.error && (
        <div className="mt-6 rounded-lg border border-danger/30 bg-danger-bg px-4 py-3 text-sm text-danger">
          Couldn't load permissions: {(caps.error as Error).message}{' '}
          <button className="underline" onClick={() => caps.refetch()}>
            Retry
          </button>
        </div>
      )}

      {!caps.isLoading && !caps.error && (
        <>
          {/* Role */}
          <div className="mt-6 max-w-xs">
            <label className="block text-sm font-medium text-ink mb-1.5">Role</label>
            <Select
              value={role}
              disabled={locked || busy}
              onChange={(e) => {
                const next = e.target.value as UserRole;
                if (next !== role) setPendingRole(next);
              }}
            >
              {isSuperAdminTarget && <option value="super_admin">{ROLE_LABELS.super_admin}</option>}
              {ASSIGNABLE_ROLES.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABELS[r]}
                </option>
              ))}
            </Select>
          </div>

          {/* Office (branch). Offices are created out-of-band (SQL) — this only
              assigns an existing one or clears it. A master-admin target has no
              tenant, so the picker is empty and stays disabled. */}
          <div className="mt-6 max-w-xs">
            <label className="block text-sm font-medium text-ink mb-1.5">Office</label>
            <Select
              value={currentBranchId}
              disabled={locked || busy || branchOptions.isLoading}
              onChange={(e) => {
                const next = e.target.value;
                if (next !== currentBranchId) void commitBranch(next);
              }}
            >
              <option value="">No office</option>
              {(branchOptions.data?.branches ?? []).map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name} ({b.code})
                </option>
              ))}
            </Select>
          </div>

          {/* Capability switches */}
          <div className="mt-6">
            <h3 className="text-sm font-medium text-ink mb-3">Permissions</h3>
            <div className="divide-y divide-line rounded-xl border border-line">
              {rows.map((row) => (
                <div
                  key={row.capability}
                  className="flex items-center justify-between gap-4 px-4 py-3"
                >
                  <div className="min-w-0">
                    <div className="text-sm text-ink">{row.label}</div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-muted">{row.tag}</span>
                      {row.overridden && !locked && (
                        <button
                          className="text-xs text-gold-deep underline hover:text-gold disabled:opacity-50"
                          disabled={busy}
                          onClick={() => handleReset(row.capability)}
                        >
                          Reset
                        </button>
                      )}
                    </div>
                  </div>
                  <Switch
                    checked={row.on}
                    disabled={locked || busy}
                    onCheckedChange={(next) => handleFlip(row.capability, next)}
                  />
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Confirms */}
      <ConfirmDialog
        open={pendingRole !== null}
        title="Change role"
        body={
          pendingRole
            ? `Change ${staff.name} from ${ROLE_LABELS[role]} to ${ROLE_LABELS[pendingRole]}? This resets their default permissions.`
            : ''
        }
        confirmLabel="Change role"
        loading={setRole.isPending}
        onConfirm={() => {
          if (pendingRole) void commitRole(pendingRole);
        }}
        onCancel={() => setPendingRole(null)}
      />
      <ConfirmDialog
        open={pendingManageUsers}
        title="Turn on Manage users"
        body={`This lets ${staff.name} manage other staff. Continue?`}
        confirmLabel="Turn on"
        loading={setOverride.isPending}
        onConfirm={async () => {
          await performFlip('manage_users', true);
          setPendingManageUsers(false);
        }}
        onCancel={() => setPendingManageUsers(false)}
      />
      <ConfirmDialog
        open={pendingSwitchOff}
        title={`Switch off ${staff.name}?`}
        body={`${staff.name} will be signed out immediately and won't be able to log in until switched back on.`}
        confirmLabel="Switch off"
        danger
        loading={deactivate.isPending}
        onConfirm={() => void confirmSwitchOff()}
        onCancel={() => setPendingSwitchOff(false)}
      />
    </div>
  );
}
